import { useReducer } from 'react';
import {
    ApplicationSortingField,
    type ApplicationSortingRule,
    SortingOrder,
    type ApplicationFilteringRule,
} from '../../../api/types';
import {
    type ExtendedPageState,
    type ExtendedPageAction,
    extendedReducer,
} from '../../../components/PaginatedTableWithContext';
import { TableSortingOrder, type SortOrderValue } from '../../../utilities/useSortAndFilterCache';

type SortRule = ApplicationSortingRule & SortOrderValue;

export type BookingListState = ExtendedPageState & {
    sort: SortRule;
    filter: ApplicationFilteringRule;
};

export type SetSortAction = { type: 'setSort'; sortBy: SortRule };
export type SetFilterAction = { type: 'setFilter'; filteringRule: ApplicationFilteringRule };
type Action = ExtendedPageAction | SetSortAction | SetFilterAction;

const reducer = (state: BookingListState, action: Action): BookingListState => {
    switch (action.type) {
        case 'setPage':
            return { ...state, page: action.page };

        case 'setPageSize':
            return { ...state, page: 0, pageSize: action.pageSize };

        case 'setSort':
            return { ...state, page: 1, sort: action.sortBy };

        case 'setFilter':
            return { ...state, page: 1, filter: action.filteringRule };

        default:
            return extendedReducer(state, action);
    }
};

export const defaultBookingListSort = {
    field: ApplicationSortingField.BookingStartDate,
    order: SortingOrder.Asc,
    orderValue: TableSortingOrder.Asc,
};

const useListReducer = (cache?: Partial<BookingListState>) =>
    useReducer(reducer, {
        companyId: cache?.companyId,
        // default pagination
        page: cache?.page || 1,
        pageSize: cache?.pageSize || 10,

        // default sorting
        sort: cache?.sort || defaultBookingListSort,

        filter: cache?.filter || {},
    });

export default useListReducer;
